/* ----------------------------------------------------------------------------------------------------------------------
   REFORMER LAB — GLOBAL ANIMATION LAYER
   ----------------------------------------------------------------------------------------------------------------------
   Drives the editorial motion system defined in rl-animations.css:
     - [data-rl-reveal] / ="mask"   revealed on scroll via IntersectionObserver
     - [data-rl-text]               split into masked words, then revealed word-by-word
     - [data-rl-stagger="N"]        cascades direct [data-rl-reveal] children (restarts every N for per-row feel)
     - [data-rl-parallax="0.15"]    subtle parallax driven by a single rAF scroll loop

   Safety: we only opt in to hiding (the `rl-ready` class on <html>) when motion is allowed AND IntersectionObserver
   exists. Otherwise we bail and the CSS leaves everything visible. Re-runs on `shopify:section:load` for the editor.
   ---------------------------------------------------------------------------------------------------------------------- */

(function () {
  var motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (motionReduced || !('IntersectionObserver' in window)) {
    return; // CSS only hides under html.rl-ready, which we never add -> everything stays visible
  }

  document.documentElement.classList.add('rl-ready');

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('rl-in');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  /* Word splitting for [data-rl-text]. Only split plain-text elements (no child elements) so we never destroy
     markup. Idempotent. */
  function splitWords(el) {
    if (el.dataset.rlSplit === 'true') return;
    if (el.childElementCount > 0) {
      el.setAttribute('data-rl-reveal', '');
      el.removeAttribute('data-rl-text');
      return;
    }

    var words = (el.textContent || '').trim().split(/\s+/);
    if (words.length === 0) return;

    var frag = document.createDocumentFragment();
    words.forEach(function (word, index) {
      var outer = document.createElement('span');
      outer.className = 'rl-word';

      var inner = document.createElement('span');
      inner.className = 'rl-word__inner';
      inner.textContent = word;
      inner.style.setProperty('--rl-i', index);

      outer.appendChild(inner);
      frag.appendChild(outer);

      if (index < words.length - 1) {
        frag.appendChild(document.createTextNode(' '));
      }
    });

    el.textContent = '';
    el.appendChild(frag);
    el.dataset.rlSplit = 'true';
  }

  function applyStagger(scope) {
    (scope || document).querySelectorAll('[data-rl-stagger]').forEach(function (group) {
      var cols = parseInt(group.getAttribute('data-rl-stagger'), 10);
      var items = group.querySelectorAll(':scope > [data-rl-reveal]');
      items.forEach(function (item, index) {
        item.style.setProperty('--rl-i', cols > 0 ? index % cols : index);
      });
    });
  }

  /* Parallax — one shared rAF loop */
  var parallaxEls = [];
  var parallaxTicking = false;

  function collectParallax(scope) {
    (scope || document).querySelectorAll('[data-rl-parallax]').forEach(function (el) {
      if (parallaxEls.indexOf(el) === -1) parallaxEls.push(el);
    });
  }

  function updateParallax() {
    var vh = window.innerHeight;
    parallaxEls.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      var factor = parseFloat(el.getAttribute('data-rl-parallax')) || 0.12;
      var offset = (rect.top + rect.height / 2 - vh / 2) * factor;
      el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    });
    parallaxTicking = false;
  }

  function requestParallax() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    window.requestAnimationFrame(updateParallax);
  }

  function observeReveals(scope) {
    (scope || document)
      .querySelectorAll('[data-rl-reveal]:not(.rl-in), [data-rl-text]:not(.rl-in)')
      .forEach(function (el) {
        observer.observe(el);
      });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-rl-text]').forEach(splitWords);
    applyStagger(scope);
    collectParallax(scope);
    observeReveals(scope);
    if (parallaxEls.length) requestParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  if ('requestAnimationFrame' in window) {
    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', requestParallax, { passive: true });
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
